import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, Copy, Users, Wallet, ArrowRight, CalendarClock, FileDown, AlertTriangle } from "lucide-react";

const CURRENCIES = ["ZAR", "USD", "EUR", "GBP"];

function emptyForm() {
  return { name: "", surname: "", phone: "", account_info: "", engagement: "monthly",
    monthly_amount: "", day_rate: "", currency: "ZAR", active: true };
}

/**
 * Worker Salaries — a separate employee list with copy-friendly account
 * details, supporting monthly and contract (per-day) engagement. Compiling
 * payroll for a period pushes ONE combined pending invoice to the Invoices
 * page. Gated by `salaries:read` (+ `salaries:manage` for edits/compile).
 */
export default function SalariesPage() {
  const { hasPerm, isMaster } = useAuth();
  const canManage = isMaster || hasPerm?.("salaries:manage");

  const [employees, setEmployees] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // employee id or null
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [compileOpen, setCompileOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [e, r] = await Promise.all([
        api.get("/salaries/employees"),
        api.get("/salaries/runs").catch(() => ({ data: [] })),
      ]);
      setEmployees(e.data || []);
      setRuns(r.data || []);
    } catch (err) {
      toast.error("Couldn't load salaries");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyForm()); setEditOpen(true); };
  const openEdit = (emp) => {
    setEditing(emp.id);
    setForm({
      name: emp.name || "", surname: emp.surname || "", phone: emp.phone || "",
      account_info: emp.account_info || "", engagement: emp.engagement || "monthly",
      monthly_amount: emp.monthly_amount ?? "", day_rate: emp.day_rate ?? "",
      currency: emp.currency || "ZAR", active: emp.active !== false,
    });
    setEditOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        monthly_amount: form.engagement === "monthly" ? Number(form.monthly_amount) || 0 : null,
        day_rate: form.engagement === "contract" ? Number(form.day_rate) || 0 : null,
      };
      if (editing) await api.put(`/salaries/employees/${editing}`, payload);
      else await api.post("/salaries/employees", payload);
      toast.success(editing ? "Employee updated" : "Employee added");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save employee");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/salaries/employees/${id}`); toast.success("Employee removed"); load(); }
    catch (_e) { toast.error("Could not remove"); }
  };

  const copyAccount = async (text) => {
    try { await navigator.clipboard.writeText(text || ""); toast.success("Account details copied"); }
    catch (_e) { toast.error("Copy failed — select and copy manually"); }
  };

  const [pdfBusy, setPdfBusy] = useState(null);
  const exportRunPDF = async (run) => {
    if (pdfBusy) return;
    setPdfBusy(run.id);
    try {
      const cur = run.currency || "ZAR";
      const rows = (run.breakdown || []).map((l) => {
        const detail = l.engagement === "contract"
          ? `${l.days_worked ?? 0} days × ${formatCurrency(l.day_rate || 0, cur)}`
          : "Monthly";
        return `| ${l.name} | ${l.engagement === "contract" ? "Contract" : "Monthly"} | ${detail} | ${formatCurrency(l.amount || 0, cur)} |`;
      });
      const period = run.start_date && run.end_date ? `${run.start_date} → ${run.end_date}` : run.label;
      const md = [
        "## Payroll summary",
        `**Period:** ${run.label}`,
        `**Dates:** ${period}`,
        `**Employees:** ${run.employee_count}`,
        `**Total:** ${formatCurrency(run.total, cur)}`,
        "",
        "| Employee | Engagement | Detail | Amount |",
        "| --- | --- | --- | --- |",
        ...rows,
        `| **Total** |  |  | **${formatCurrency(run.total, cur)}** |`,
      ].join("\n");
      const { downloadAssistantPDF } = await import("@/lib/assistantReport");
      await downloadAssistantPDF(md, { question: `Payroll — ${run.label}`, kicker: "PAYROLL · SUMMARY" });
      toast.success("Payroll PDF downloaded");
    } catch (e) {
      toast.error(`Could not create the PDF: ${e?.message || e}`);
    } finally { setPdfBusy(null); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="salaries-page">
      <PageHeader
        title="Salaries"
        subtitle="Employees, pay details, and payroll that compiles into an invoice"
        actions={
          canManage && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCompileOpen(true)} data-testid="salaries-compile-open">
                <Wallet className="h-4 w-4 mr-2" /> Compile payroll
              </Button>
              <Button size="sm" onClick={openAdd} data-testid="salaries-add-employee"
                className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90">
                <Plus className="h-4 w-4 mr-2" /> Add employee
              </Button>
            </div>
          )
        }
      />

      <Card className="rounded-xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Users className="h-4 w-4 text-[hsl(var(--ap-brass))]" /> Employees
            <span className="text-xs text-muted-foreground font-normal">· {employees.length}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-6">Loading…</p>
          ) : employees.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6" data-testid="salaries-empty">
              No employees yet.{canManage ? " Add your first with “Add employee”." : ""}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="salaries-employees-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b">
                    <th className="py-2 font-medium">Name</th>
                    <th className="py-2 font-medium">Phone</th>
                    <th className="py-2 font-medium">Engagement</th>
                    <th className="py-2 font-medium text-right">Pay</th>
                    <th className="py-2 font-medium">Account details</th>
                    {canManage && <th className="py-2 font-medium text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => (
                    <tr key={emp.id} className="border-b border-border/40" data-testid={`salaries-row-${emp.id}`}>
                      <td className="py-2.5 font-medium">
                        {emp.name} {emp.surname}
                        {emp.active === false && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">inactive</span>}
                      </td>
                      <td className="py-2.5 text-muted-foreground">{emp.phone || "—"}</td>
                      <td className="py-2.5">
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] border border-border">
                          {emp.engagement === "contract" ? "Contract · per day" : "Monthly"}
                        </span>
                      </td>
                      <td className="py-2.5 text-right tabular-nums">
                        {emp.engagement === "contract"
                          ? `${formatCurrency(emp.day_rate || 0, emp.currency || "ZAR")}/day`
                          : formatCurrency(emp.monthly_amount || 0, emp.currency || "ZAR")}
                      </td>
                      <td className="py-2.5 max-w-[280px]">
                        <div className="flex items-start gap-2">
                          <span className="text-xs text-muted-foreground whitespace-pre-wrap break-words line-clamp-2" title={emp.account_info || ""}>
                            {emp.account_info || "—"}
                          </span>
                          {emp.account_info && (
                            <button onClick={() => copyAccount(emp.account_info)} title="Copy account details"
                              className="shrink-0 text-muted-foreground hover:text-[hsl(var(--ap-forest))]" data-testid={`salaries-copy-${emp.id}`}>
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      {canManage && (
                        <td className="py-2.5 text-right whitespace-nowrap">
                          <button onClick={() => openEdit(emp)} className="text-muted-foreground hover:text-foreground p-1" data-testid={`salaries-edit-${emp.id}`} aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                          <ConfirmDialog
                            title="Remove this employee?"
                            description="This removes them from the salaries list. Past payroll invoices are unaffected."
                            confirmLabel="Remove"
                            onConfirm={() => remove(emp.id)}
                          >
                            <button className="text-muted-foreground hover:text-destructive p-1" data-testid={`salaries-delete-${emp.id}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                          </ConfirmDialog>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payroll run history */}
      {runs.length > 0 && (
        <Card className="rounded-xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-[hsl(var(--ap-saddle))]" /> Payroll history
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="salaries-runs">
              {runs.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-lg border p-3" data-testid={`salaries-run-${r.id}`}>
                  <div>
                    <div className="font-medium text-sm">{r.label}</div>
                    <div className="text-[11px] text-muted-foreground">{r.employee_count} employees · pushed to invoice</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-lg tabular-nums">{formatCurrency(r.total, r.currency || "ZAR")}</span>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground"
                      onClick={() => exportRunPDF(r)} disabled={pdfBusy === r.id} data-testid={`salaries-run-pdf-${r.id}`}>
                      {pdfBusy === r.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileDown className="h-3 w-3 mr-1" />}
                      PDF
                    </Button>
                    <Link to="/invoices" className="text-xs text-[hsl(var(--ap-forest))] hover:underline inline-flex items-center gap-1">
                      Invoices <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add / edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="salaries-employee-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit employee" : "Add employee"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name" /></div>
              <div><Label className="text-xs">Surname</Label><Input value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} data-testid="employee-surname" /></div>
            </div>
            <div><Label className="text-xs">Phone number</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="employee-phone" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Engagement</Label>
                <Select value={form.engagement} onValueChange={(v) => setForm({ ...form, engagement: v })}>
                  <SelectTrigger data-testid="employee-engagement"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly (flat)</SelectItem>
                    <SelectItem value="contract">Contract (per day)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="employee-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {form.engagement === "monthly" ? (
              <div><Label className="text-xs">Monthly amount</Label><Input type="number" value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })} data-testid="employee-monthly" /></div>
            ) : (
              <div><Label className="text-xs">Day rate</Label><Input type="number" value={form.day_rate} onChange={(e) => setForm({ ...form, day_rate: e.target.value })} data-testid="employee-dayrate" /></div>
            )}
            <div>
              <Label className="text-xs">Account details (bank, account no., branch — anything to copy-paste)</Label>
              <Textarea rows={3} value={form.account_info} onChange={(e) => setForm({ ...form, account_info: e.target.value })}
                placeholder={"Standard Bank\nAcc: 000 000 000\nBranch: 12345"} data-testid="employee-account" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="employee-save"
              className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CompilePayrollDialog
        open={compileOpen}
        onOpenChange={setCompileOpen}
        employees={employees}
        onDone={load}
      />
    </div>
  );
}

function CompilePayrollDialog({ open, onOpenChange, employees, onDone }) {
  const active = useMemo(() => employees.filter((e) => e.active !== false), [employees]);
  const contract = active.filter((e) => e.engagement === "contract");
  const monthly = active.filter((e) => e.engagement !== "contract");
  const [days, setDays] = useState({});
  const [label, setLabel] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [currency, setCurrency] = useState("ZAR");
  const [busy, setBusy] = useState(false);
  const [dupMsg, setDupMsg] = useState(null);

  useEffect(() => { if (open) { setDays({}); setLabel(""); setStart(""); setEnd(""); setDupMsg(null); } }, [open]);

  const total = useMemo(() => {
    let t = 0;
    for (const e of monthly) t += Number(e.monthly_amount) || 0;
    for (const e of contract) t += (Number(e.day_rate) || 0) * (Number(days[e.id]) || 0);
    return Math.round(t * 100) / 100;
  }, [monthly, contract, days]);

  const submit = async (force = false) => {
    if (total <= 0) { toast.error("Nothing to compile yet — set amounts / days worked"); return; }
    setBusy(true);
    if (force) setDupMsg(null);
    try {
      const lines = contract.map((e) => ({ employee_id: e.id, days_worked: Number(days[e.id]) || 0 }));
      const res = await api.post("/salaries/compile", {
        start_date: start || null, end_date: end || null,
        label: label || null, currency, lines, force,
      });
      toast.success(`Payroll compiled — ${formatCurrency(res.data?.invoice?.total ?? total, currency)} invoice created`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      if (e?.response?.status === 409) {
        setDupMsg(e?.response?.data?.detail || "A payroll already exists for this period.");
      } else {
        toast.error(e?.response?.data?.detail || "Could not compile payroll");
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="salaries-compile-dialog">
        <DialogHeader><DialogTitle>Compile payroll</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Start</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="compile-start" /></div>
            <div><Label className="text-xs">End</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="compile-end" /></div>
            <div>
              <Label className="text-xs">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="compile-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs">Label (optional)</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="March 2026 payroll" data-testid="compile-label" /></div>

          {contract.length > 0 && (
            <div className="rounded-lg border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Contract staff — days worked</div>
              <div className="space-y-2">
                {contract.map((e) => (
                  <div key={e.id} className="flex items-center justify-between gap-2">
                    <span className="text-sm">{e.name} {e.surname} <span className="text-muted-foreground text-xs">· {formatCurrency(e.day_rate || 0, e.currency || currency)}/day</span></span>
                    <Input type="number" className="w-24 h-8" value={days[e.id] ?? ""} placeholder="days"
                      onChange={(ev) => setDays({ ...days, [e.id]: ev.target.value })} data-testid={`compile-days-${e.id}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
            <span className="text-sm text-muted-foreground">{monthly.length} monthly · {contract.length} contract</span>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total to invoice</div>
              <div className="font-serif text-2xl tabular-nums" data-testid="compile-total">{formatCurrency(total, currency)}</div>
            </div>
          </div>

          {dupMsg && (
            <div className="rounded-lg border border-[hsl(var(--ap-warning)/0.5)] bg-[hsl(var(--ap-warning)/0.08)] p-3" data-testid="compile-dup-warning">
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--ap-warning))]" />
                <span>{dupMsg}</span>
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setDupMsg(null)} data-testid="compile-dup-cancel">Keep as is</Button>
                <Button size="sm" onClick={() => submit(true)} disabled={busy} data-testid="compile-dup-force"
                  className="bg-[hsl(var(--ap-warning))] hover:bg-[hsl(var(--ap-warning))]/90 text-white">
                  {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Compile anyway
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => submit(false)} disabled={busy || total <= 0 || !!dupMsg} data-testid="compile-submit"
            className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Push to Invoices
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
